import { useEffect, useState, type KeyboardEvent, type SyntheticEvent } from 'react';
import { Badge, Stack, Text, Tooltip, type MantineSize } from '@mantine/core';
import { IconClock, IconClockPlus } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import type { OpeningStatus } from '../../../lib/api/domains/customerCare';
import { OPENING_STATUS_QUERY_KEY, describeStatus } from './openingStatusText';
import { OpeningHoursModal } from './OpeningHoursModal';

export { OPENING_STATUS_QUERY_KEY };

export interface OpeningStatusIdent {
  customerId?: string | null;
  weclappCustomerId?: string | null;
  cwCompanyId?: number | null;
}


function identKey(ident: OpeningStatusIdent): string | null {
  if (ident.customerId) return `c:${ident.customerId}`;
  if (ident.weclappCustomerId) return `w:${ident.weclappCustomerId}`;
  if (ident.cwCompanyId != null) return `cw:${ident.cwCompanyId}`;
  return null;
}

/** Tickt alle 30 s, damit „in 5 Min" nicht stehen bleibt, während der Status gleich ist. */
function useNow(intervalMs = 30_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function useCustomerOpeningStatus(ident: OpeningStatusIdent, options: { enabled?: boolean } = {}) {
  const key = identKey(ident);
  return useQuery({
    queryKey: [OPENING_STATUS_QUERY_KEY, key],
    queryFn: () =>
      api.getCustomerOpeningStatus({
        customerId: ident.customerId ?? undefined,
        weclappCustomerId: ident.weclappCustomerId ?? undefined,
        cwCompanyId: ident.cwCompanyId ?? undefined,
      }),
    enabled: (options.enabled ?? true) && key != null,
    // Der Status kippt an Fenstergrenzen; eine Minute Verzug ist im Support unerheblich.
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: false,
  });
}

function TooltipBody({ status }: { status: OpeningStatus }) {
  return (
    <Stack gap={4}>
      <Text size="xs" fw={600}>
        Heute: {status.today.length ? status.today.join(', ') : 'geschlossen'}
      </Text>
      <Text size="xs">{status.summary}</Text>
      {status.note && (
        <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>
          {status.note}
        </Text>
      )}
      {status.timeZone !== 'Europe/Berlin' && (
        <Text size="xs" c="dimmed">
          Zeiten in {status.timeZone}
        </Text>
      )}
    </Stack>
  );
}

/**
 * „Geöffnet bis 17:00" / „Pause bis 13:00 · in 25 Min" / „Geschlossen · öffnet Mo 08:00".
 *
 * Mit Kunden-Kennung (`ident`) holt das Abzeichen den Status selbst und ist klickbar:
 * Der Klick öffnet den Pflege-Dialog — mit „Von KIAN recherchieren" —, auch und gerade
 * dann, wenn noch nichts hinterlegt ist. Dafür steht ohne Zeiten ein dezentes
 * „Öffnungszeiten?" da, statt gar nichts: Wer gerade mit dem Kunden zu tun hat, ist
 * der Richtige, um die Lücke zu schließen.
 *
 * Mit fertigem `status` (Kundeninfos haben ihn schon geladen) ist es reine Anzeige;
 * gepflegt wird dort über die Karte daneben.
 */
export function CustomerOpeningStatusBadge({
  status: givenStatus,
  ident,
  size = 'sm',
  showUnknown,
}: {
  status?: OpeningStatus;
  ident?: OpeningStatusIdent;
  size?: MantineSize;
  /** Standard: nur, wenn das Abzeichen klickbar ist (also mit `ident`). */
  showUnknown?: boolean;
}) {
  const query = useCustomerOpeningStatus(ident ?? {}, { enabled: !givenStatus && !!ident });
  const now = useNow();
  const [editOpen, setEditOpen] = useState(false);
  const status = givenStatus ?? query.data?.status;
  if (!status) return null;
  const editable = !givenStatus && !!query.data;
  const unknown = status.state === 'UNKNOWN';
  if (unknown && !(showUnknown ?? editable)) return null;

  const openEditor = (e: SyntheticEvent) => {
    // Abzeichen sitzen oft in klickbaren Zeilen/Links (Kundenname öffnet die Akte).
    e.stopPropagation();
    e.preventDefault();
    setEditOpen(true);
  };

  const view = describeStatus(status, now);
  const label = unknown ? 'Öffnungszeiten?' : `${view.label}${view.detail ? ` · ${view.detail}` : ''}`;
  const tooltip = unknown ? (
    <Text size="xs">
      Keine Öffnungszeiten hinterlegt.{editable ? ' Klicken: von Hand eintragen oder von KIAN recherchieren lassen.' : ''}
    </Text>
  ) : (
    <Stack gap={6}>
      <TooltipBody status={status} />
      {editable && (
        <Text size="xs" c="dimmed">
          Klicken zum Bearbeiten
        </Text>
      )}
    </Stack>
  );

  return (
    <>
      <Tooltip label={tooltip} multiline w={280} withinPortal>
        <Badge
          size={size}
          variant={unknown ? 'outline' : 'light'}
          color={view.color}
          leftSection={unknown ? <IconClockPlus size={11} /> : <IconClock size={11} />}
          // span statt button: Das Abzeichen sitzt teils IN einem Knopf (Kunden-Hub-Leiste),
          // und ein button im button ist ungültiges HTML.
          component="span"
          role={editable ? 'button' : undefined}
          tabIndex={editable ? 0 : undefined}
          onClick={editable ? openEditor : undefined}
          onKeyDown={
            editable
              ? (e: KeyboardEvent<HTMLSpanElement>) => {
                  if (e.key === 'Enter' || e.key === ' ') openEditor(e);
                }
              : undefined
          }
          style={{
            textTransform: 'none',
            cursor: editable ? 'pointer' : 'help',
            flexShrink: 0,
            opacity: unknown ? 0.8 : undefined,
          }}
          aria-label={unknown ? 'Öffnungszeiten hinterlegen' : `Kunde: ${label}`}
        >
          {label}
        </Badge>
      </Tooltip>
      {editable && query.data && (
        // React reicht Ereignisse aus dem Portal an die Vorfahren im Baum weiter — ohne
        // diese Hülle öffnete ein Klick im Dialog die Zeile oder Akte dahinter.
        <span
          style={{ display: 'contents' }}
          onClick={(e) => e.stopPropagation()}
        >
          <OpeningHoursModal
            opened={editOpen}
            onClose={() => setEditOpen(false)}
            customerId={query.data.customerId}
            customerName={query.data.customerName}
            value={query.data.openingHours}
          />
        </span>
      )}
    </>
  );
}
